import { type ReactElement } from 'react';

import { type Event, EventTypeEnum } from '@clubspark-node/clubspark-tennis-types';
import { participantConstants, tournamentEngine } from 'tods-competition-factory';

import { type ScaleAttributes } from 'components/panels/eventsSplitDraws/selectedEvent/rankingsAndRatings/types';
import { type ParticipantId, type ParticipantInterface, type ScaleItem } from 'typedefs/store/tmxTypes';
import { TodsFactoryError } from 'utils/error';

import NTRPScaling from './ntrp';
import PlayTrackerScaling from './pt';
import RankingScaling from './ranking';
import type Scaling from './Scaling';
import WTNScaling from './wtn';

const { PAIR, TEAM } = participantConstants;

export type ScalingType = 'WTN' | 'NTRP' | 'PT' | 'Ranking';
export type SubScalingType = 'WTN Singles' | 'WTN Doubles' | 'PT Orange play' | 'PT Green win' | 'PT Green play';
export type ScalingTypeOptions = Record<string, any>;
export type ScalingPurpose = 'SEED' | 'SELECTION' | 'TABLE_SORT';

export interface ScalingOptions {
  event?: Event;
  scaleName?: string | ((scalingType: ScalingType, scaleName: string) => string);
  drawId?: string; // could be flight drawId
  policyDefinitions?: any; // TODO: Type?
  ptValueType?: string;
}

export interface ParticipantScaling<T extends ScaleItem = ScaleItem> {
  participant: ParticipantInterface;
  scaleItems: T[];
  isAllScaleItemsPresent: boolean;
}

export interface GetScalingOptionsParams {
  scalingType?: ScalingType;
  subScalingType?: ScalingType | SubScalingType;
  drawId?: string;
}

// TODO: Move this somewhere. This is a definition for a factory specific type.
export interface ScaledEntry {
  participantId: ParticipantId;
  value: string | number;
  order?: number;
}

/**
 * Accepts the string name of a scalingType and returns the associated instance
 * @param scalingType The name of a scaling type
 * @returns The instance of a scaling type
 */
export const scalingTypeToScaling = (scalingType: ScalingType): typeof Scaling | undefined => {
  switch (scalingType) {
    case 'Ranking':
      return RankingScaling as typeof Scaling;
    case 'NTRP':
      return NTRPScaling as typeof Scaling;
    case 'WTN':
      return WTNScaling as typeof Scaling;
    case 'PT':
      return PlayTrackerScaling as typeof Scaling;
  }
};

export const getEventTypeFromSubScaling = (subScalingType: ScalingType | SubScalingType) =>
  subScalingType === 'WTN Singles'
    ? EventTypeEnum.SINGLES
    : subScalingType === 'WTN Doubles'
      ? EventTypeEnum.DOUBLES
      : undefined;

export const getPtValueTypeFromSubScaling = (scalingType: ScalingType, subScalingType: ScalingType | SubScalingType) =>
  scalingType === 'PT' ? subScalingType : undefined;

export const defaultScaleNameFn = (scalingType: ScalingType, scaleName: string, options?: ScalingOptions) => {
  if (scalingType === 'PT') {
    const isPtGreen = options?.ptValueType === 'PT Green win' || options?.ptValueType === 'PT Green play';
    return `${scaleName}.${isPtGreen ? 'GREEN' : 'ORANGE'}`;
  }
  return scaleName;
};

/**
 *
 * @param scalingType The name of a scaling type
 * @param participant The participant object
 * @param options Any additional options
 * @returns Returns a object that contains the original participant and any associated scaleItems
 */
export const getParticipantScaling = (
  scalingType: ScalingType,
  participant: ParticipantInterface,
  options: Pick<ScalingOptions, 'event' | 'scaleName'>,
): ParticipantScaling => {
  const scaling = scalingTypeToScaling(scalingType);
  const scaleNameFn = options?.scaleName ?? defaultScaleNameFn;
  const scaleAttributes = scaling.getScaleAttributes(
    (scaleName: string) =>
      typeof scaleNameFn === 'function' ? scaleNameFn(scalingType, scaleName, options) : scaleNameFn,
    options?.event,
  );
  const scaleItemKey = `SCALE.${scaleAttributes.scaleType}.${scaleAttributes.eventType}.${scaleAttributes.scaleName}`;
  const isPair = participant?.participantType === PAIR;
  const isTeam = participant?.participantType === TEAM;
  const participants = isPair || isTeam ? participant?.individualParticipants ?? [] : [participant];

  const scaleItems: ScaleItem[] = participants
    .map((participant) => {
      const scaleValue = participant?.timeItems?.findLast((item) => item?.itemType === scaleItemKey)?.itemValue;
      if (scaleValue)
        return {
          scaleName: scaleAttributes.scaleName,
          eventType: scaleAttributes.eventType,
          scaleValue,
        };

      if (typeof scaling.defaultScaleValue === 'undefined') return undefined;

      return {
        scaleName: scaleAttributes.scaleName,
        eventType: scaleAttributes.eventType,
        scaleValue: scaling.defaultScaleValue,
      };
    })
    .filter((scaleItem) => typeof scaleItem !== 'undefined');
  const isAllScaleItemsPresent = (!isPair || scaleItems.length === 2) && scaleItems.length === participants.length;
  return {
    participant,
    scaleItems,
    isAllScaleItemsPresent,
  };
};

/**
 *
 * @param purpose The purpose to determine the calculation method used
 * @param participants An array of participant objects
 * @param scalingType The name of a scaling type
 * @param options Any additional options
 * @param seedsCount An optional number to truncate the entries returned
 * @param scalingTypeOptions Unique scaling type options
 * @returns An array of ScaledEntry for the given parameters
 */
export const getScaledEntries = (
  participants: ParticipantInterface[],
  scalingType: ScalingType,
  options: ScalingOptions,
  scalingTypeOptions?: ScalingTypeOptions,
  purpose: ScalingPurpose = 'SEED',
  seedsCount = Infinity,
): ScaledEntry[] => {
  const scaleSortMethod = getScaleSortMethod(scalingType, purpose, scalingTypeOptions);
  const scaledEntries: ScaledEntry[] = participants
    .map((participant) => getParticipantScaling(scalingType, participant, options))
    .sort(scaleSortMethod)
    .map((participantScaling, index) => ({
      participantId: participantScaling?.participant?.participantId,
      value: getParticipantScalingValue(scalingType, participantScaling),
      order: index + 1,
    }));

  return scaledEntries.slice(
    0,
    Math.min(seedsCount, scaledEntries.filter(({ value }) => Number.isFinite(value)).length),
  );
};

/**
 * This performs seeding calculations on the given parameters and returns and object containing new scaleItems for seeding. These values are not persisted at this point.
 * @param scalingType The name of a scaling type
 * @param scaledEntries An array of ScaledEntry
 * @param drawSize The size of the draw
 * @param drawStage The stage of the draw
 * @param options Any additional options
 * @returns An object containing scaleItemsWithParticipantIds which represents the seeding values
 */
export const seed = (
  scalingType: ScalingType,
  scaledEntries: ScaledEntry[],
  drawSize: number,
  drawStage,
  { drawId, scaleName, event, policyDefinitions }: ScalingOptions,
): {
  scaleAttributes: ScaleAttributes;
  scaleItemsWithParticipantIds: {
    participantId: string;
    scaleItems: ScaleItem[];
  };
} => {
  const { error, seedsCount, stageEntries } = tournamentEngine.getEntriesAndSeedsCount({
    drawId,
    drawSize,
    eventId: event?.eventId,
    policyDefinitions,
    stage: drawStage,
  });
  if (error) throw new TodsFactoryError(error);

  const scaling = scalingTypeToScaling(scalingType);
  const scaleNameFn = scaleName ?? defaultScaleNameFn;
  const scaleAttributes = scaling.getScaleAttributes(
    (scaleName: string) => (typeof scaleNameFn === 'function' ? scaleNameFn(scalingType, scaleName) : scaleNameFn),
    event,
  );

  const { scaleItemsWithParticipantIds } = tournamentEngine.generateSeedingScaleItems({
    scaledEntries,
    seedsCount,
    scaleAttributes,
    scaleName: drawId || event?.eventId, // scaleName defines the name of the generated SEEDING scale (TARGET SCALE)
    stageEntries,
  });

  return { scaleAttributes, scaleItemsWithParticipantIds };
};

/**
 *
 * @param scalingType The name of a scaling type
 * @param participantScaling An object that contains the original participant and any associated scaleItems
 * @param scalingTypeOptions Unique scaling type options
 * @returns A JSX compatible value representing the value used to display in the DOM
 */
export const getParticipantScalingDisplay = (
  scalingType: ScalingType,
  participantScaling: ParticipantScaling,
  scalingTypeOptions?: ScalingTypeOptions,
): string | ReactElement => {
  const scaling = scalingTypeToScaling(scalingType);
  return scaling.getDisplay(participantScaling, scalingTypeOptions);
};

/**
 *
 * @param scalingType The name of a scaling type
 * @param participantScaling An object that contains the original participant and any associated scaleItems
 * @param scalingTypeOptions Unique scaling type options
 * @returns A plain string value that represents what the user would normally see
 */
export const getParticipantScalingText = (
  scalingType: ScalingType,
  participantScaling: ParticipantScaling,
  scalingTypeOptions?: ScalingTypeOptions,
): string => {
  const scaling = scalingTypeToScaling(scalingType);
  return scaling.getText(participantScaling, scalingTypeOptions);
};

/**
 *
 * @param scalingType The name of a scaling type
 * @param participantScaling An object that contains the original participant and any associated scaleItems
 * @param scalingTypeOptions Additional options
 * @returns A value that represents the actual internal value that is not normally displayed to the user
 */
export const getParticipantScalingValue = (
  scalingType: ScalingType,
  participantScaling: ParticipantScaling,
  scalingTypeOptions?: ScalingTypeOptions,
): string | number => {
  const scaling = scalingTypeToScaling(scalingType);
  return scaling.getValue(participantScaling, scalingTypeOptions);
};

export const getScaleSortMethod = (
  scalingType: ScalingType,
  purpose: ScalingPurpose,
  scalingTypeOptions?: ScalingTypeOptions,
): ((a: ParticipantScaling, b: ParticipantScaling) => number) => {
  const scaling = scalingTypeToScaling(scalingType);
  return (a, b) => scaling.scaleSortMethod(purpose, a, b, scalingTypeOptions);
};

/**
 *
 * @param scalingType The name of a scaling type
 * @param options Any additional options
 * @returns  A plain string value of the scale name to display to the user
 */
export const getScaleDisplayName = (scalingType: ScalingType, options?: ScalingOptions): string => {
  const scaling = scalingTypeToScaling(scalingType);
  return scaling.getDisplayName(options);
};

/**
 *
 * @param scalingType The name of a scaling type
 * @returns An array of SubScaling names or undefined
 */
export const getSubScalings = (scalingType: ScalingType): SubScalingType[] | undefined => {
  const scaling = scalingTypeToScaling(scalingType);
  return scaling?.getSubScalings();
};
